import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AUTH_API_BASE_URL = process.env.REACT_APP_AUTH_API_BASE_URL || `${API_BASE_URL}/api/auth`;

const AdminProfilePage = () => {
    const navigate = useNavigate();
    const adminUser = useMemo(() => {
        try {
            const raw = localStorage.getItem('guest_admin_user');
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            return null;
        }
    }, []);

    const fullName = [adminUser?.first_name, adminUser?.last_name].filter(Boolean).join(' ').trim();
    const displayName = fullName || adminUser?.username || 'Administrator';
    const roleLabel = adminUser?.is_superuser ? 'Super Admin' : 'Admin';
    const [securityForm, setSecurityForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isEndingSessions, setIsEndingSessions] = useState(false);
    const [securityError, setSecurityError] = useState('');
    const [securitySuccess, setSecuritySuccess] = useState('');

    const profileRows = [
        { key: 'Name', value: displayName },
        { key: 'Username', value: adminUser?.username || '-' },
        { key: 'Email', value: adminUser?.email || '-' },
        { key: 'Phone', value: adminUser?.phone || '-' },
        { key: 'Role', value: roleLabel },
        { key: 'Status', value: adminUser?.is_active === false ? 'Inactive' : 'Active' },
    ];

    const getAccessToken = () => (
        localStorage.getItem('guest_access_token') ||
        sessionStorage.getItem('guest_access_token') ||
        ''
    ).trim();

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setSecurityForm((prev) => ({ ...prev, [name]: value }));
        if (securityError) {
            setSecurityError('');
        }
        if (securitySuccess) {
            setSecuritySuccess('');
        }
    };

    const handleChangePassword = async (event) => {
        event.preventDefault();
        setSecurityError('');
        setSecuritySuccess('');

        if (!securityForm.currentPassword || !securityForm.newPassword || !securityForm.confirmPassword) {
            setSecurityError('All password fields are required.');
            return;
        }

        if (securityForm.newPassword.length < 8) {
            setSecurityError('New password must be at least 8 characters.');
            return;
        }

        if (securityForm.newPassword !== securityForm.confirmPassword) {
            setSecurityError('New password and confirm password do not match.');
            return;
        }

        const accessToken = getAccessToken();
        if (!accessToken) {
            setSecurityError('Session expired. Please log in again.');
            return;
        }

        setIsSaving(true);
        try {
            const response = await fetch(`${AUTH_API_BASE_URL}/change-password/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    current_password: securityForm.currentPassword,
                    new_password: securityForm.newPassword,
                    confirm_password: securityForm.confirmPassword,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                setSecurityError(data.error || 'Unable to change password.');
                return;
            }

            setSecuritySuccess(data.message || 'Password changed successfully.');
            setSecurityForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            setSecurityError('Unable to reach server right now.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogoutAllDevices = async () => {
        setSecurityError('');
        setSecuritySuccess('');

        const accessToken = getAccessToken();
        const refreshToken = (localStorage.getItem('guest_refresh_token') || sessionStorage.getItem('guest_refresh_token') || '').trim();

        setIsEndingSessions(true);
        try {
            if (accessToken && refreshToken) {
                await fetch(`${AUTH_API_BASE_URL}/logout/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({ refresh_token: refreshToken }),
                });
            }
        } finally {
            localStorage.removeItem('guest_user_role');
            localStorage.removeItem('guest_access_token');
            localStorage.removeItem('guest_refresh_token');
            localStorage.removeItem('guest_admin_user');
            sessionStorage.removeItem('guest_user_role');
            sessionStorage.removeItem('guest_access_token');
            sessionStorage.removeItem('guest_refresh_token');
            setIsEndingSessions(false);
            navigate('/login');
        }
    };

    return (
        <div className="admin-profile-grid">
            <div className="admin-panel-card admin-profile-card">
                <div className="admin-profile-head">
                    <div className="admin-profile-avatar">{displayName.charAt(0).toUpperCase()}</div>
                    <div>
                        <h3 className="admin-profile-name">{displayName}</h3>
                        <p className="admin-profile-role">{roleLabel}</p>
                    </div>
                </div>

                <div className="admin-profile-meta">
                    {profileRows.map((row) => (
                        <div className="admin-profile-meta-row" key={row.key}>
                            <span>{row.key}</span>
                            <strong>{row.value}</strong>
                        </div>
                    ))}
                </div>
            </div>

            <div className="admin-panel-card admin-profile-card">
                <h3>Security</h3>
                <p className="admin-subtitle">Manage your account access and session settings.</p>

                <form className="admin-security-form" onSubmit={handleChangePassword}>
                    <label className="admin-security-label" htmlFor="currentPassword">Current Password</label>
                    <input
                        id="currentPassword"
                        name="currentPassword"
                        type="password"
                        className="admin-security-input"
                        value={securityForm.currentPassword}
                        onChange={handleInputChange}
                        disabled={isSaving || isEndingSessions}
                    />

                    <label className="admin-security-label" htmlFor="newPassword">New Password</label>
                    <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        className="admin-security-input"
                        value={securityForm.newPassword}
                        onChange={handleInputChange}
                        disabled={isSaving || isEndingSessions}
                    />

                    <label className="admin-security-label" htmlFor="confirmPassword">Confirm New Password</label>
                    <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        className="admin-security-input"
                        value={securityForm.confirmPassword}
                        onChange={handleInputChange}
                        disabled={isSaving || isEndingSessions}
                    />

                    {securityError ? <div className="admin-security-feedback admin-security-feedback-error">{securityError}</div> : null}
                    {securitySuccess ? <div className="admin-security-feedback admin-security-feedback-success">{securitySuccess}</div> : null}

                    <div className="admin-profile-action-list">
                        <button
                            type="submit"
                            className="admin-profile-action-btn admin-profile-action-btn-primary"
                            disabled={isSaving || isEndingSessions}
                        >
                            {isSaving ? 'Updating Password...' : 'Change Password'}
                        </button>
                        <button
                            type="button"
                            className="admin-profile-action-btn admin-profile-action-btn-danger"
                            onClick={handleLogoutAllDevices}
                            disabled={isSaving || isEndingSessions}
                        >
                            {isEndingSessions ? 'Ending Sessions...' : 'Log Out All Devices'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminProfilePage;